/**
 * public-url — the single composer for a tenant-scoped public event/registration
 * URL, now backed by the contracted `getTenantPublicConfig` read (kit
 * ee7551b2 / REQ-011). Host resolution is owned by the data layer; the shell
 * global (`__STUDIO_PUBLIC_ROOT__`) fallback is DROPPED per the 2026-07-07
 * geo/settings handoff — `getTenantPublicConfig` is the single source of truth.
 *
 * `loadPublicConfig()` fetches once and caches; `publicHost()`/`publicUrl()`
 * read the cache synchronously (empty until the first load resolves — call
 * `useTenantPublicConfig()` in any screen that renders a public URL so the
 * value fills in). Prefer the contracted `getPublicEventPage().publicUrl` for a
 * specific event's canonical link; this composer is the tenant-level prefix +
 * slug display form.
 */
import { useEffect, useState } from "react";
import { getTenantPublicConfig, type StudioSettingsRequestOptions } from "@v0y4ge/api-client";
import { can, getActiveTenantSlug, onTenantChange, onTenantSwitching } from "../shell/access";

export type PublicHostPrefix = "attend" | "book" | "join" | "meet" | "public" | "onestop";

/**
 * Tenant id → DEPLOYMENT slug, FROM SERVED CONFIG ONLY (docket 2026-09-18
 * item 7).
 *
 * The two strings are not always the same and the public hosts belong to the
 * second: a deployment served at `<host>` (Studio on `studio.<host>.<root>`,
 * so `join.<host>.<root>`) can carry a longer tenant id `<host>-<suffix>`, and
 * every composed host then named a hostname that resolves to nothing
 * (changelog 2026-09-14T1810-cc-report-0057…).
 *
 * ⛔ THE MAPPING IS NOT IN SOURCE. It used to be a literal
 * `{ "<tenant-id>": "<host>" }`, which put ONE tenant's slug into the
 * public AllJourneys chunk served to EVERY tenant — the platform is
 * tenant-agnostic by rule, and a per-tenant value belongs in the tenant's own
 * served configuration. `getTenantPublicConfig` now answers it as
 * `deploymentSlug` (CD-API-REQUEST 2026-09-18); absent, the tenant slug IS the
 * deployment slug, which is correct for a stock deployment and is what every
 * other tenant already got.
 */
function deploySlug(tenant: string): string {
  const slug = (_cfg && _cfg.deploymentSlug) || "";
  if (!slug || !tenant) return tenant;
  const seed = (_cfg && _cfg.tenantSlug) || "";
  // No declared id → the served slug is this deployment's own; a declared id
  // that is not the tenant asked about leaves that tenant's slug alone.
  return !seed || seed === tenant ? slug : tenant;
}
/** Rewrite a SERVED host that carries the tenant id where the deployment slug belongs. */
function withDeploySlug(host: string): string {
  const seed = (_cfg && _cfg.tenantSlug) || "";
  const slug = (_cfg && _cfg.deploymentSlug) || "";
  if (!host || !seed || !slug || seed === slug) return host;
  if (host.includes(`.${seed}.`)) return host.replace(`.${seed}.`, `.${slug}.`);
  if (host === seed) return slug;
  if (host.startsWith(`${seed}.`)) return `${slug}.${host.slice(seed.length + 1)}`;
  if (host.endsWith(`.${seed}`)) return `${host.slice(0, -seed.length)}${slug}`;
  return host;
}

interface PublicConfig {
  publicHost: string;
  attendHost: string;
  /**
   * The queue/walk-in surface's own authority, resolved server-side by the
   * canonical `publicBaseUrlFor(queue)` — so a tenant's custom join domain and
   * a local `localhost:18812` both arrive here WHOLE, port included.
   *
   * OPTIONAL because a deployment that has not configured a join surface has
   * none. There is deliberately no composed substitute: see `publicHost`.
   */
  joinHost?: string;
  /**
   * The One-Stop portal surface's own authority, resolved server-side by the
   * canonical `publicBaseUrlFor(portal)` — so a tenant's custom portal domain
   * and a port arrive here WHOLE, exactly as `joinHost` does.
   *
   * OPTIONAL, and unlike `joinHost` it is genuinely COMPOSABLE when absent:
   * `onestop` is a fixed prefix on the tenant's own domain, so the generic
   * `<prefix>.<tenant>.<rootDomain>` branch in `publicHost` is already right
   * for a stock deployment. The served value is still preferred, because only
   * it can know a custom domain or a port.
   */
  onestopHost?: string;
  rootDomain: string;
  prefixes?: string[];
  /**
   * The tenant id these hosts were resolved for, and the slug the deployment is
   * actually served at. Both OPTIONAL: a deployment whose id and slug agree has
   * nothing to declare, and absent means "they agree" — never a guess.
   * Requested 2026-09-18 (docket item 7) to replace a source-side literal.
   */
  tenantSlug?: string;
  deploymentSlug?: string;
  /**
   * CAPABILITY FLAGS THE DEPLOYMENT DECLARES FOR ITSELF (docket 2026-09-18b).
   *
   * A surface that is withheld during a rollout is a property of the
   * deployment, not a fact about a tenant's name — so it is served, never
   * compiled in. Absent, or a flag that is absent, reads FALSE: a capability
   * nobody stated is one nobody has.
   *
   * Declared and consumed today: `customLandingTemplate` (the drag-and-resize
   * landing canvas). Requested 2026-09-19; no server states it yet, so the
   * surface is withheld everywhere until one does — which is exactly what the
   * platform's hand patch did on every import.
   */
  features?: Record<string, boolean>;
}
const OPTS: StudioSettingsRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};

let _cfg: PublicConfig | null = null;
let _loading = false;
const _subs = new Set<() => void>();
const _notify = () => _subs.forEach((f) => f());
onTenantSwitching(() => { _cfg = null; _loading = false; _notify(); });
onTenantChange(() => { if (can("settings.read")) loadPublicConfig(); }); // admin/settings/public-config — gated (docket 2026-09-17); screens that render a public URL still warm it on demand via useTenantPublicConfig()

/** Fetch the tenant public config once (idempotent); notifies subscribers on resolve. */
export function loadPublicConfig(): void {
  if (_cfg || _loading || typeof getTenantPublicConfig !== "function") return;
  _loading = true;
  (getTenantPublicConfig as (o: StudioSettingsRequestOptions) => Promise<unknown>)(OPTS)
    .then((r: unknown) => { const raw = r as Record<string, any>; _cfg = (raw?.data ?? raw) as PublicConfig; _notify(); })
    .catch((e: unknown) => console.warn("getTenantPublicConfig failed:", e))
    .finally(() => { _loading = false; });
}

/** React hook: ensures the config is loaded and re-renders when it arrives. */
export function useTenantPublicConfig(): PublicConfig | null {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _subs.add(fn);
    loadPublicConfig();
    return () => { _subs.delete(fn); };
  }, []);
  return _cfg;
}

/** Subscribe outside React (class screens). Warms the config; returns unsubscribe. */
export function onPublicConfigChange(fn: () => void): () => void {
  _subs.add(fn);
  loadPublicConfig();
  return () => { _subs.delete(fn); };
}

/**
 * Is this deployment declared capable of `name`? FALSE until the served config
 * says otherwise — never a tenant slug, never a build-time list.
 */
export function tenantFeature(name: string): boolean {
  return !!(_cfg && _cfg.features && _cfg.features[name] === true);
}

/** The server's rule for an event's public route when it has no landing slug: normalizedPublicRouteSegment
 *  (packages/journey-runtime/src/public-hosts.ts, CC 0413). Trim, lower-case, then each run of characters
 *  other than a-z and 0-9 (hyphens included) becomes one "-", trimmed at both ends. "2027 AT&T Pebble Beach
 *  Pro-Am" → "2027-at-t-pebble-beach-pro-am"; "Foo--Bar" → "foo-bar". Only a fallback: prefer the address
 *  the server assigned (publicUrlForSurface). */
export function slugify(s: string): string {
  return (s || "").toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

/** Awaitable, time-bounded version of loadPublicConfig — for callers that are
 *  about to COMPOSE a fallback URL outside React (no ancestor has warmed the
 *  cache via useTenantPublicConfig). Resolves as soon as the config lands, or
 *  after `timeoutMs` with whatever is cached (possibly null) — the composer
 *  then degrades exactly as before. Fixes the smoke-caught race where a popup
 *  briefly landed on `attend.your-workspace/<slug>` (2026-07-09 amendment). */
export function ensurePublicConfig(timeoutMs = 3000): Promise<PublicConfig | null> {
  if (_cfg) return Promise.resolve(_cfg);
  loadPublicConfig();
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const done = () => { _subs.delete(fn); if (timer) clearTimeout(timer); resolve(_cfg); };
    const fn = () => done();
    _subs.add(fn);
    timer = setTimeout(done, timeoutMs);
  });
}

/** The tenant-scoped public host, e.g. "attend.acme-live.v0y4ge.com".
 *  Sourced from the contracted config; composed from rootDomain + slug only if
 *  the config hasn't loaded yet (returns a bare `prefix.slug` when neither is
 *  available — never a hardcoded vanity domain). */
export function publicHost(prefix: PublicHostPrefix = "attend", tenantSlug?: string | null): string {
  loadPublicConfig();
  /**
   * `join` is SERVED, never composed. It is the only prefix whose authority
   * comes from one field and nothing else.
   *
   * The composed branch below produces `<prefix>.<tenant>.<rootDomain>`, which
   * for a queue meant `join.<tenant>.<rootDomain>` — a hostname that resolves
   * to nothing, while the tenant's real walk-in surface was on a local
   * `localhost:<port>`. A published Walk-Ins link pointed at the invented
   * one. `publicBaseUrlFor(queue)` already knows the answer,
   * custom hosts and ports included, and the backend now returns it as
   * `joinHost`.
   *
   * Absent — the config has not resolved, or the deployment serves no join
   * surface — this returns "". Callers gate on the empty string and show
   * nothing rather than a link that goes nowhere. That is the whole point: an
   * unresolved authority must not be guessed.
   */
  if (prefix === "join") return withDeploySlug((_cfg && _cfg.joinHost) || "");
  /**
   * `onestop` PREFERS the served host and otherwise FALLS THROUGH — the one
   * place this resolver deliberately differs from `join` above.
   *
   * Only the served `onestopHost` can know a custom portal domain or a port.
   * But when it is absent — an events worker older than this bundle, or a
   * deployment that serves no portal surface — the composed
   * `onestop.<tenant>.<rootDomain>` below is CORRECT for a stock tenant,
   * because `onestop` is a fixed prefix on the tenant's own domain. So unlike
   * `join` there is no reason to return "" here, and doing so would blank a
   * link that works today.
   */
  if (prefix === "onestop" && _cfg && _cfg.onestopHost) return withDeploySlug(_cfg.onestopHost);
  if (_cfg) {
    if (prefix === "attend" && _cfg.attendHost) return withDeploySlug(_cfg.attendHost);
    if (_cfg.rootDomain) {
      const t = deploySlug((tenantSlug ?? getActiveTenantSlug()) || "");
      return t ? `${prefix}.${t}.${_cfg.rootDomain}` : withDeploySlug(_cfg.publicHost || `${prefix}.${_cfg.rootDomain}`);
    }
    if (prefix === "attend" && _cfg.publicHost) return withDeploySlug(_cfg.publicHost);
  }
  const t = deploySlug((tenantSlug ?? getActiveTenantSlug()) || "your-workspace");
  return `${prefix}.${t}`;
}

/** Full public URL for an event/page slug. `bare` omits the scheme for display chrome. */
export function publicUrl(slug: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  const host = publicHost(opts?.prefix ?? "attend", opts?.tenantSlug);
  const path = `${host}/${slug || "event"}`;
  return opts?.bare ? path : `https://${path}`;
}

/** The address the server assigned (a journey or event's `publicUrl`, else its `slug`); composed from the
 *  title only when neither exists yet (CC 0396). */
export function publicUrlForSurface(surface: { publicUrl?: string | null; public_url?: string | null; slug?: string | null } | null | undefined, fallbackTitle: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  const url = String(surface?.publicUrl ?? surface?.public_url ?? "").trim();
  if (/^https?:\/\//i.test(url)) return opts?.bare ? url.replace(/^https?:\/\//i, "") : url;
  const slug = String(surface?.slug ?? "").trim();
  return slug ? publicUrl(slug, opts) : publicUrlForTitle(fallbackTitle, opts);
}

/** Compose from a display title (slugifies first). */
export function publicUrlForTitle(title: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  return publicUrl(slugify(title), opts);
}

/**
 * utm_source / utm_medium values for links Studio copies. Identifiers, not copy:
 * never translated, never shown as text.
 */
export const SHARE_SOURCES = { // ui-config
  eventsTable: "studio-events-table",
  eventsCalendar: "studio-events-calendar",
  eventOverlay: "studio-event-overlay",
  landingPages: "studio-landing-pages",
  landingBuilder: "studio-landing-builder",
  hubs: "studio-hubs",
  journeyShare: "studio-journey-share",
  journeysList: "studio-journeys-list",
  appointments: "studio-appointments",
  eventsPage: "studio-events-page",
  eventLanding: "studio-event-landing",
  walkIns: "studio-walkins",
  queues: "studio-queues",
  onestop: "studio-onestop",
} as const;
export type ShareSource = (typeof SHARE_SOURCES)[keyof typeof SHARE_SOURCES];
export const SHARE_MEDIUM = { link: "share-link", embed: "embed" } as const; // ui-config

export interface ShareUtm { source: string; medium: string; campaign?: string; content?: string }

/**
 * Tag a public link with where in Studio it was shared from.
 *
 * - Keeps every existing parameter and the hash.
 * - A URL that already carries ANY utm_* parameter is returned unchanged, so a
 *   tracked link (the event Share tab builds its own) is never re-labelled or mixed.
 * - Returns the input unchanged when it is not an absolute http(s) URL (a bare
 *   display host such as "attend.x.y/slug" is never guessed into a URL here), or
 *   when source or medium is empty.
 * - Empty campaign / content are left out.
 */
export function withUtm(url: string, utm: ShareUtm): string {
  const raw = String(url || "").trim();
  if (!/^https?:\/\//i.test(raw) || !utm.source || !utm.medium) return url;
  let u: URL;
  try { u = new URL(raw); } catch { return url; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return url;
  for (const key of u.searchParams.keys()) {
    if (key.toLowerCase().startsWith("utm_")) return url;
  }
  const put = (k: string, v?: string) => {
    const value = String(v ?? "").trim();
    if (value) u.searchParams.set(`utm_${k}`, value);
  };
  put("source", utm.source);
  put("medium", utm.medium);
  put("campaign", utm.campaign);
  put("content", utm.content);
  return u.toString();
}
